import {Color} from '@benchling/nucleus-ui';
import {ActionIcon, createStyles, useMantineTheme} from '@mantine/core';
import React from 'react';
import {type Edge, EdgeLabelRenderer, type EdgeProps, MarkerType} from 'reactflow';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import BaseEdgeRenderer from 'ui-library/reactflow/edges/BaseEdgeRenderer';
import {EdgeType, LineStyle} from 'ui-library/reactflow/edges/edgeOptions';
import {getEdgeMidpointLabelCoords} from 'ui-library/reactflow/edges/ElkSplineEdgeRenderer';

import {useProcedureRunDesignFlowchartEdgeActions} from './procedureRunDesignFlowchartEdgeActionsContext';
import {useProcedureRunDesignFlowchartIsEditable} from './procedureRunDesignFlowchartEditableContext';
import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {getProcedureRunDesignFlowchartNodeHandleId} from './ProcedureRunDesignFlowchartNodeHandle';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE = 'procedureRunDesignFlowchartEdge';

/**
 * Visual variant for a design flowchart edge. Add a variant plus a style entry to introduce new tones.
 * `readonly` is the lighter tone for edges whose source is at or after the active unit-op layer.
 * `preview` is the dashed blue tone for add-replicate preview edges.
 * `archived` is the disabled gray tone for inert edges: persisted archives and edges caught up in a lane
 * queued for removal this session.
 */
export type ProcedureRunDesignFlowchartEdgeVariant = 'editable' | 'readonly' | 'preview' | 'archived';

export interface ProcedureRunDesignFlowchartEdgeData {
  variant: ProcedureRunDesignFlowchartEdgeVariant;
  /**
   * Whether this edge is in the upstream-to-active position and can be removed while editing.
   * Mirrors the drawing affordance so draw and delete stay in sync. Defaults to false.
   */
  isRemovable?: boolean;
}

/** Whether a variant renders inert: no hover tint, no selection, no remove button. Mirrors `isInert` on the node. */
export function isInertProcedureRunDesignFlowchartEdgeVariant(
  variant: ProcedureRunDesignFlowchartEdgeVariant
): boolean {
  return variant === 'archived';
}

/**
 * Whether an edge counts as a live connection and not archived.
 */
export function isLiveProcedureRunDesignFlowchartEdge(
  edge: Edge<ProcedureRunDesignFlowchartEdgeData>
): boolean {
  return !isInertProcedureRunDesignFlowchartEdgeVariant(edge.data?.variant ?? 'editable');
}

/** How a variant draws: stroke color plus solid or dashed. */
interface ProcedureRunDesignFlowchartEdgeStyle {
  color: string;
  lineStyle: LineStyle;
}

/** The single lookup for edge appearance. Add a variant here to introduce a new tone. */
const EDGE_VARIANT_STYLES: Record<
  ProcedureRunDesignFlowchartEdgeVariant,
  ProcedureRunDesignFlowchartEdgeStyle
> = {
  editable: {color: Color.GRAY_600, lineStyle: LineStyle.SOLID},
  readonly: {color: Color.GRAY_400, lineStyle: LineStyle.SOLID},
  // Dashes are reserved for an edge that does not exist yet.
  preview: {color: Color.BLUE_200, lineStyle: LineStyle.DASHED},
  // The disabled gray for inert edges.
  archived: {color: Color.GRAY_400, lineStyle: LineStyle.SOLID},
};

/** Solid vs dashed stroke for a variant. */
function getProcedureRunDesignFlowchartEdgeLineStyle(
  variant: ProcedureRunDesignFlowchartEdgeVariant
): LineStyle {
  return EDGE_VARIANT_STYLES[variant].lineStyle;
}

/** Stroke/marker color for a variant. ReactFlow markers are per-edge, so arrowheads match the line. */
export function getProcedureRunDesignFlowchartEdgeColor(
  variant: ProcedureRunDesignFlowchartEdgeVariant
): string {
  return EDGE_VARIANT_STYLES[variant].color;
}

const PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR = '->';

export function getProcedureRunDesignFlowchartEdgeId(sourceNodeId: string, targetNodeId: string): string {
  return `${sourceNodeId}${PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR}${targetNodeId}`;
}

/**
 * Inverse of `getProcedureRunDesignFlowchartEdgeId`. Node ids never contain the separator, so the id
 * splits cleanly into its source and target node ids.
 */
export function parseProcedureRunDesignFlowchartEdgeId(edgeId: string): {
  sourceNodeId: string;
  targetNodeId: string;
} {
  const separatorIndex = edgeId.indexOf(PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR);
  assert(separatorIndex !== -1, `Malformed flowchart edge id: ${edgeId}`);
  return {
    sourceNodeId: edgeId.slice(0, separatorIndex),
    targetNodeId: edgeId.slice(separatorIndex + PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_ID_SEPARATOR.length),
  };
}

/** Options controlling edge removability. */
export interface ProcedureRunDesignFlowchartEdgeFlags {
  /** Whether the edge is in the upstream-to-active position and removable while editing. */
  isRemovable?: boolean;
}

export function makeProcedureRunDesignFlowchartEdge(
  spec: FlowchartEdgeSpec,
  {isRemovable = false}: ProcedureRunDesignFlowchartEdgeFlags = {}
): Edge<ProcedureRunDesignFlowchartEdgeData> {
  const variant = spec.variant ?? 'editable';
  return {
    id: getProcedureRunDesignFlowchartEdgeId(spec.sourceNodeId, spec.targetNodeId),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE,
    source: spec.sourceNodeId,
    target: spec.targetNodeId,
    sourceHandle: getProcedureRunDesignFlowchartNodeHandleId(spec.sourceNodeId, 'source'),
    targetHandle: getProcedureRunDesignFlowchartNodeHandleId(spec.targetNodeId, 'target'),
    data: {variant, isRemovable: isRemovable && !isInertProcedureRunDesignFlowchartEdgeVariant(variant)},
    markerEnd: {type: MarkerType.Arrow, color: getProcedureRunDesignFlowchartEdgeColor(variant)},
  };
}

/**
 * Renders a design flowchart edge as a bezier curve colored by its variant. Removable edges in
 * edit mode tint blue on hover and show a midpoint trash button when selected. All other edges
 * render inert.
 */
export default function ProcedureRunDesignFlowchartEdge(
  props: EdgeProps<ProcedureRunDesignFlowchartEdgeData>
): JSX.Element {
  const theme = useMantineTheme();
  const isEditable = useProcedureRunDesignFlowchartIsEditable();
  const variant = props.data?.variant ?? 'editable';
  // An inert variant can never be edited
  const canEdit =
    isEditable &&
    (props.data?.isRemovable ?? false) &&
    !isInertProcedureRunDesignFlowchartEdgeVariant(variant);
  const isSelected = canEdit && (props.selected ?? false);
  return (
    <>
      <BaseEdgeRenderer
        {...props}
        selected={isSelected}
        data={{
          type: EdgeType.BEZIER,
          color: getProcedureRunDesignFlowchartEdgeColor(variant),
          lineStyle: getProcedureRunDesignFlowchartEdgeLineStyle(variant),
          hoverColor: canEdit ? theme.colors.blue[4] : undefined,
        }}
      />
      {isSelected && <ProcedureRunDesignRemoveEdgeLabel {...props} />}
    </>
  );
}

/**
 * Midpoint trash button for a selected removable edge, rendered via EdgeLabelRenderer.
 * Reads onRemoveEdge from context so the handler stays stable.
 */
function ProcedureRunDesignRemoveEdgeLabel(props: EdgeProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {onRemoveEdge} = useProcedureRunDesignFlowchartEdgeActions();
  const {labelX, labelY} = getEdgeMidpointLabelCoords(props);
  return (
    <EdgeLabelRenderer>
      <div
        // Only the transform is dynamic (depends on the edge midpoint); static styles live in classes.
        style={{transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`}}
        className={cx(classes.labelContainer, 'nopan')}
      >
        <ActionIcon
          variant="tertiary"
          size="sm"
          onClick={() => {
            onRemoveEdge(props.id);
          }}
          aria-label="Remove edge"
          {...getTestProps({component: 'ProcedureRunDesignFlowchartEdge', element: 'removeButton'})}
        >
          <BenchlingIcon icon="trash-can" />
        </ActionIcon>
      </div>
    </EdgeLabelRenderer>
  );
}

const useStyles = createStyles(() => ({
  labelContainer: {
    position: 'absolute',
    pointerEvents: 'all',
  },
}));

export const EDGE_TYPES = {
  [PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE]: ProcedureRunDesignFlowchartEdge,
};
